import { useEffect, useState, useMemo } from "react";
import axios from "axios";

import Header from "../../components/Header";
import HorizontalRow from "../../components/HorizontalRow";
import SkeletonCard from "../../components/SkeletonCard";
import ProfilePanel from "../../components/ProfilePanel";
import HeroCarousel from "../../components/HeroCarousel";

import { collection, getDocs } from "firebase/firestore";
import { db } from "../../auth/firebase";

import {
  fetchTrendingMovies,
  fetchLatestMovies,
  fetchTopRatedMovies,
  searchMovies,
} from "../../api/tmdb";

import { moods } from "../../utils/moods";

/* ================= LANGUAGES ================= */
const LANGUAGES = [
  { label: "All", value: "all" },
  { label: "Telugu", value: "te" },
  { label: "Tamil", value: "ta" },
  { label: "Hindi", value: "hi" },
  { label: "Malayalam", value: "ml" },
  { label: "Kannada", value: "kn" },
  { label: "English", value: "en" },
];

/* ================= OTT CONFIG ================= */
const OTT_CONFIG = {
  Netflix: {
    key: "Netflix",
    title: "Netflix Movies to Watch",
    logo: "https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg",
  },
  "Amazon Prime Video": {
    key: "Amazon Prime Video",
    title: "Prime Movies to Watch",
    logo: "https://upload.wikimedia.org/wikipedia/commons/f/f1/Prime_Video.png",
  },
  Aha: {
    key: "Aha",
    title: "Aha Movies to Watch",
    logo: "https://images.seeklogo.com/logo-png/46/2/aha-ott-logo-png_seeklogo-463455.png",
  },
  "Sony LIV": {
    key: "Sony LIV",
    title: "SonyLIV Movies to Watch",
    logo: "https://upload.wikimedia.org/wikipedia/commons/2/24/Sony_LIV_logo.png",
  },
  ZEE5: {
    key: "ZEE5",
    title: "ZEE5 Movies to Watch",
    logo: "https://upload.wikimedia.org/wikipedia/commons/0/0e/ZEE5_logo.png",
  },
};

/* ================= ROW TITLE ================= */
const RowTitle = ({ logo, text }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    {logo && <img src={logo} alt={text} style={{ height: 22 }} />}
    <span>{text}</span>
  </div>
);

/* ================= DB NORMALIZER ================= */
const normalizeBackendMovie = (movie) => {
  const imagePath = movie.posterUrl
    ? movie.posterUrl.replace("https://image.tmdb.org/t/p/original", "")
    : null;

  return {
    id: Number(movie.tmdbId),
    title: movie.title,
    poster_path: imagePath,
    backdrop_path: imagePath,
    overview: movie.overview || "Now streaming on OTT",
    genre_ids: movie.genre_ids || [],
    vote_average: movie.vote_average ?? 0,
    release_date: movie.releaseDate || null,
    original_language: movie.original_language || movie.language,
    languages: Array.isArray(movie.languages) ? movie.languages : [],
    ottProviders: Array.isArray(movie.ottProviders) ? movie.ottProviders : [],
    editorialTag: movie.editorialTag || null,
    priority: movie.priority ?? 99,
  };
};

/* ================= FILTER HELPERS ================= */
const matchesLanguage = (movie, language) => {
  if (language === "all") return true;
  if (movie.original_language === language) return true;
  return movie.languages?.includes(language);
};

const matchesMood = (movie, moodId) => {
  if (!moodId) return true;
  return movie.genre_ids?.includes(Number(moodId));
};

function Movies() {
  const [language, setLanguage] = useState("all");
  const [selectedMood, setSelectedMood] = useState(null);

  const [trending, setTrending] = useState([]);
  const [latest, setLatest] = useState([]);
  const [topRated, setTopRated] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dbMovies, setDbMovies] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);

  const selectedMoodId = selectedMood ? Number(selectedMood) : null;
  const [filteredResults, setFilteredResults] = useState([]);

  /* ================= TMDB LOAD ================= */
  useEffect(() => {
    Promise.all([
      fetchTrendingMovies(),
      fetchLatestMovies(),
      fetchTopRatedMovies(),
    ]).then(([t, l, r]) => {
      setTrending(t);
      setLatest(l);
      setTopRated(r);
      setLoading(false);
    });
  }, []);

  /* ================= FIREBASE LOAD ================= */
  useEffect(() => {
    const loadDbMovies = async () => {
      const snapshot = await getDocs(collection(db, "movies"));
      const movies = snapshot.docs.map((doc) =>
        normalizeBackendMovie(doc.data())
      );
      setDbMovies(movies);
    };
    loadDbMovies();
  }, []);

  /* ================= SEARCH ================= */
  useEffect(() => {
    if (searchQuery.length > 2) {
      searchMovies(searchQuery).then(setSearchResults);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  /* ================= MERGED MOVIE POOL ================= */
  const allMovies = useMemo(() => {
    const map = {};
    [...trending, ...latest, ...topRated, ...dbMovies].forEach((m) => {
      if (m?.id) map[m.id] = m;
    });
    return Object.values(map);
  }, [trending, latest, topRated, dbMovies]);

  /* ================= WATCHITNOW ================= */
  const watchItNowMovies = allMovies.filter(
    (m) =>
      m.editorialTag === "watchitnow" &&
      matchesLanguage(m, language) &&
      matchesMood(m, selectedMoodId)
  );

  /* ================= OTT FILTER ================= */
  const getOttMovies = (provider) =>
    dbMovies.filter(
      (m) =>
        m.ottProviders?.some((p) => p.provider_name === provider) &&
        matchesLanguage(m, language) &&
        matchesMood(m, selectedMoodId)
    );

  const heroItems = dbMovies.filter((m) => m.backdrop_path).slice(0, 10);

  const clearFilters = () => {
    setLanguage("all");
    setSelectedMood(null);
    setSearchQuery("");
    setSearchResults([]);
    setFilteredResults([]);
  };

  return (
    <>
      <Header
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onProfile={() => setProfileOpen(true)}
      />

      {/* LANGUAGE */}
      <div className="language-bar">
  {LANGUAGES.map((l) => (
    <button
      key={l.value}
      className={`lang-btn ${language === l.value ? "active" : ""}`}
      onClick={() => setLanguage(l.value)}
    >
      {l.label}
    </button>
  ))}

  {(language !== "all" || searchQuery || selectedMood) && (
    <button className="clear-filters-btn" onClick={clearFilters}>
      Clear ✕
    </button>
  )}
</div>

      {/* MOODS */}
      <div className="mood-bar">
        {moods.map((m) => (
          <button
            key={m.id}
            className={`mood-btn ${
              Number(m.id) === selectedMoodId ? "active" : ""
            }`}
            onClick={() => setSelectedMood(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <HeroCarousel items={heroItems} />

      {/* SEARCH */}
      {searchResults.length > 0 && (
        <HorizontalRow title="Search Results" movies={searchResults} />
      )}

      {/* WATCHITNOW */}
      {watchItNowMovies.length > 0 && (
        <HorizontalRow
          title={<RowTitle text="✨ WatchItNow Team Suggested" />}
          movies={watchItNowMovies.slice(0, 12)}
        />
      )}

      {/* OTT SECTIONS */}
      {Object.values(OTT_CONFIG).map((ott) => {
        const movies = getOttMovies(ott.key).slice(0, 12);
        if (!movies.length) return null;

        return (
          <HorizontalRow
            key={ott.key}
            title={<RowTitle logo={ott.logo} text={ott.title} />}
            movies={movies}
          />
        );
      })}

      {/* STANDARD ROWS */}
      {loading ? (
        <div className="scroll-row">
          {Array(6)
            .fill(0)
            .map((_, i) => (
              <SkeletonCard key={i} />
            ))}
        </div>
      ) : (
        <>
          <HorizontalRow title="Trending Now" movies={trending.slice(0, 20)} />
          <HorizontalRow title="Latest Releases" movies={latest.slice(0, 20)} />
          <HorizontalRow title="Top Rated" movies={topRated.slice(0, 20)} />
        </>
      )}

      <ProfilePanel open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

export default Movies;
